// features/projects/templates/continuum/hypotheses.js
// ═══════════════════════════════════════════════════════════
// CONTINUUM HYPOTHESES — parallel hypothesis registry
//
// Lists every parallel hypothesis registered against findings.
// Each hypothesis is a paired claim (eco-side + human-side) bound
// by a shared mechanism, with explicit refuters.
//
// Grouped by status: PROPOSED -> ACTIVE -> REFINED -> REFUTED.
// Within each section, sorted by registered_at desc.
//
// Inline status flow: PROPOSED can advance to ACTIVE / REFINED /
// REFUTED. Each transition takes an optional status_note.
//
// Session 6 of the Continuum-actionability extension. Session 7
// will add cross-references between hypotheses and predictions.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { C, FONT as F } from './tokens';
import { DYNAMICS } from '../../../../lib/continuum/simulation/methodology';

const DYN_BY_KEY = Object.fromEntries(DYNAMICS.map(d => [d.key, d]));

const STATUS_COLOR = {
  proposed: C.gold,
  active:   C.teal,
  refined:  C.sage,
  refuted:  C.terra,
};

const STATUS_LABEL = {
  proposed: 'PROPOSED',
  active:   'ACTIVE',
  refined:  'REFINED',
  refuted:  'REFUTED',
};

const STATUS_DESCRIPTION = {
  proposed: 'Registered but not yet acted on.',
  active:   'Under active testing — evidence is being gathered.',
  refined:  'Reformulated based on evidence; the original claim was modified.',
  refuted:  'Disconfirmed by evidence — the parallel does not hold.',
};

const STATUS_ORDER = ['proposed', 'active', 'refined', 'refuted'];

export default function ContinuumHypotheses({ workspaceId }) {
  const [hypotheses, setHypotheses] = useState(null);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    setError(null);
    try {
      const r = await fetch(`/api/continuum/hypotheses/list?workspace_id=${workspaceId}`);
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      const j = await r.json();
      setHypotheses(j.hypotheses || []);
    } catch (err) {
      setError(err.message);
      setHypotheses([]);
    }
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  const loading = hypotheses === null;
  const grouped = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = (hypotheses || []).filter(h => h.status === s);
    return acc;
  }, {});
  const counts = Object.fromEntries(STATUS_ORDER.map(s => [s, grouped[s]?.length || 0]));

  return (
    <div style={{
      height: '100%', background: C.bg, color: C.text,
      fontFamily: F.mono, fontSize: 12,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <Header loading={loading} onReload={reload} counts={counts} />
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '18px 22px' }}>
        {loading ? (
          <Empty msg="Loading hypotheses…" />
        ) : error ? (
          <Empty msg={`Error: ${error}`} tone="error" />
        ) : (hypotheses || []).length === 0 ? (
          <Empty msg="No parallel hypotheses registered yet. Open the Findings tab, expand a finding with a human translation, and click 'Register the human-system parallel hypothesis'." />
        ) : (
          <>
            {STATUS_ORDER.map(status => grouped[status].length > 0 && (
              <Section
                key={status}
                title={`${STATUS_LABEL[status]} (${grouped[status].length})`}
                description={STATUS_DESCRIPTION[status]}
                accent={STATUS_COLOR[status]}
              >
                {grouped[status].map(h => (
                  <HypothesisCard
                    key={h.id}
                    hypothesis={h}
                    workspaceId={workspaceId}
                    onChanged={reload}
                  />
                ))}
              </Section>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────
// Matches features/projects/templates/continuum/findings.js Header
// pattern: pill label + context line + Refresh button.
function Header({ loading, onReload, counts }) {
  const accent = C.teal;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', padding: '0 20px', height: 42,
      borderBottom: `1px solid ${C.b1}`, background: C.s1, flexShrink: 0, gap: 14,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 9, padding: '3px 10px', borderRadius: 4,
        background: 'rgba(77,184,184,.10)', border: '1px solid rgba(77,184,184,.25)',
        color: accent,
      }}>
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: accent }} />
        Hypotheses — Parallel Claims Registry
      </div>
      {counts && (
        <div style={{ fontSize: 9, color: C.dim }}>
          {STATUS_ORDER.map(s => `${counts[s]} ${STATUS_LABEL[s].toLowerCase()}`).join(' · ')}
        </div>
      )}
      <div style={{ marginLeft: 'auto' }}>
        <button
          onClick={onReload}
          disabled={loading}
          style={{
            fontSize: 9, padding: '5px 14px', borderRadius: 4,
            background: 'transparent', border: `1px solid ${C.b2}`,
            color: C.static, cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: F.mono, letterSpacing: '0.06em',
          }}
        >
          {loading ? '⟳ Loading' : '↻ Refresh'}
        </button>
      </div>
    </div>
  );
}

// ─── Empty / loading state ────────────────────────────────────
function Empty({ msg, tone }) {
  return (
    <div style={{
      padding: '40px 20px', textAlign: 'center',
      color: tone === 'error' ? C.terra : C.dim,
      fontFamily: F.sans, fontSize: 13, lineHeight: 1.6,
      maxWidth: 600, margin: '40px auto',
    }}>
      {msg}
    </div>
  );
}

// ─── Section wrapper ──────────────────────────────────────────
function Section({ title, description, accent, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{
        marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${C.b1}`,
      }}>
        <div style={{
          fontFamily: F.mono, fontSize: 9, letterSpacing: '0.16em',
          color: accent, textTransform: 'uppercase', fontWeight: 600,
          marginBottom: 4,
        }}>
          ◆ {title}
        </div>
        {description && (
          <div style={{
            fontFamily: F.sans, fontSize: 10, color: C.dim, fontStyle: 'italic',
          }}>
            {description}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {children}
      </div>
    </div>
  );
}

// ─── Hypothesis card ──────────────────────────────────────────
function HypothesisCard({ hypothesis, workspaceId, onChanged }) {
  const [statusOpen, setStatusOpen] = useState(false);
  const dyn = DYN_BY_KEY[hypothesis.dynamic_key];
  const dynLabel = dyn?.label || hypothesis.dynamic_key || 'unknown dynamic';
  const statusColor = STATUS_COLOR[hypothesis.status] || C.dim;
  const statusLabel = STATUS_LABEL[hypothesis.status] || hypothesis.status?.toUpperCase();

  return (
    <div style={{
      padding: '12px 14px',
      background: 'rgba(255,255,255,.02)',
      border: `1px solid ${C.b1}`, borderRadius: 5,
    }}>
      {/* Top strip */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        marginBottom: 10, fontFamily: F.mono, fontSize: 9,
      }}>
        <span style={{
          padding: '2px 8px', borderRadius: 3,
          border: `1px solid ${statusColor}60`,
          background: `${statusColor}15`, color: statusColor,
          letterSpacing: '0.08em', fontWeight: 600, textTransform: 'uppercase',
        }}>
          {statusLabel}
        </span>
        <span style={{ color: C.mid, letterSpacing: '0.04em' }}>{dynLabel}</span>
        <span style={{ marginLeft: 'auto', color: C.dim, letterSpacing: '0.04em' }}>
          registered {formatDate(hypothesis.registered_at)}
        </span>
      </div>

      {/* Eco / Human claims */}
      <div style={{ display: 'grid', gap: 8, marginBottom: 8 }}>
        <ClaimRow label="Eco" text={hypothesis.eco_claim} accent={C.sage} />
        <ClaimRow label="Human" text={hypothesis.human_claim} accent={C.gold} />
      </div>

      {/* Shared mechanism */}
      {hypothesis.shared_mechanism && (
        <div style={{
          marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${C.b1}`,
        }}>
          <FieldHeading text="Shared mechanism" />
          <FieldBody text={hypothesis.shared_mechanism} />
        </div>
      )}

      {/* Refuters */}
      {hypothesis.testable_predictions && (
        <div style={{
          marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${C.b1}`,
        }}>
          <FieldHeading text="Refuters" />
          <FieldBody text={hypothesis.testable_predictions} />
        </div>
      )}

      {/* Linked forward predictions (cross-domain evidence trail) */}
      {(hypothesis.linked_predictions || []).length > 0 && (
        <div style={{
          marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${C.b1}`,
        }}>
          <FieldHeading text={`Linked predictions (${hypothesis.linked_predictions.length})`} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
            {hypothesis.linked_predictions.map(p => (
              <LinkedPredictionRow key={p.id} prediction={p} />
            ))}
          </div>
        </div>
      )}

      {/* Status note (after a transition) */}
      {hypothesis.status_note && (
        <div style={{
          marginTop: 10, padding: '8px 10px',
          background: `${statusColor}08`,
          border: `1px solid ${statusColor}30`, borderRadius: 4,
        }}>
          <div style={{
            fontFamily: F.mono, fontSize: 8, letterSpacing: '0.14em',
            color: statusColor, textTransform: 'uppercase', marginBottom: 4,
          }}>
            Status note · {formatDate(hypothesis.status_changed_at)}
          </div>
          <div style={{
            fontSize: 11, color: C.mid, lineHeight: 1.5,
            fontFamily: F.sans, whiteSpace: 'pre-wrap',
          }}>
            {hypothesis.status_note}
          </div>
        </div>
      )}

      {/* Status transition button (only when not refuted) */}
      {hypothesis.status !== 'refuted' && (
        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
          {!statusOpen ? (
            <button
              type="button"
              onClick={() => setStatusOpen(true)}
              style={{
                background: 'transparent', border: `1px solid ${C.b2}`, borderRadius: 3,
                padding: '5px 14px', fontFamily: F.mono, fontSize: 10,
                letterSpacing: '0.06em', color: C.dim, textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              Update status
            </button>
          ) : (
            <StatusTransitionForm
              hypothesis={hypothesis}
              workspaceId={workspaceId}
              onCancel={() => setStatusOpen(false)}
              onUpdated={() => {
                setStatusOpen(false);
                onChanged?.();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Status transition form ───────────────────────────────────
function StatusTransitionForm({ hypothesis, workspaceId, onCancel, onUpdated }) {
  // Allowed transitions per current status. proposed -> any of the
  // non-proposed states; active <-> refined; either can go to refuted.
  const allowed = {
    proposed: ['active', 'refined', 'refuted'],
    active:   ['refined', 'refuted'],
    refined:  ['active', 'refuted'],
  }[hypothesis.status] || [];

  const [chosen, setChosen] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!chosen || saving) return;
    setSaving(true);
    setError(null);
    try {
      const r = await fetch('/api/continuum/hypotheses/update-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: workspaceId,
          hypothesis_id: hypothesis.id,
          status: chosen,
          status_note: note.trim() || null,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      onUpdated?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      width: '100%', padding: '12px',
      background: 'rgba(200,169,110,.04)',
      border: `1px solid ${C.gold}40`, borderRadius: 4,
    }}>
      <div style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em',
        color: C.gold, textTransform: 'uppercase', marginBottom: 10, fontWeight: 600,
      }}>
        ◆ Update status
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {allowed.map(s => (
          <button
            key={s}
            type="button"
            onClick={() => setChosen(s)}
            style={{
              flex: 1,
              padding: '6px 10px',
              background: chosen === s ? `${STATUS_COLOR[s]}20` : 'transparent',
              border: `1px solid ${chosen === s ? STATUS_COLOR[s] : C.b2}`,
              borderRadius: 3,
              color: chosen === s ? STATUS_COLOR[s] : C.mid,
              fontFamily: F.mono, fontSize: 10, letterSpacing: '0.06em',
              textTransform: 'uppercase', cursor: 'pointer', fontWeight: 600,
              minWidth: 100,
            }}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      <label style={{ display: 'block' }}>
        <span style={{
          display: 'block',
          fontFamily: F.mono, fontSize: 8, letterSpacing: '0.14em',
          color: C.dim, textTransform: 'uppercase', marginBottom: 4,
        }}>
          Note (optional — what evidence drove this transition?)
        </span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Cite specific data, dates, sources."
          style={{
            width: '100%', background: 'rgba(255,255,255,.02)',
            border: `1px solid ${C.b1}`, borderRadius: 3,
            padding: '6px 8px', fontFamily: 'inherit', fontSize: 12,
            color: C.text, resize: 'vertical', outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </label>

      {error && (
        <div style={{
          marginTop: 8, padding: '6px 10px',
          background: 'rgba(196,80,80,.08)',
          border: '1px solid rgba(196,80,80,.40)',
          borderRadius: 3, fontSize: 11, color: C.terra, fontFamily: F.mono,
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          style={{
            background: 'transparent', border: `1px solid ${C.b1}`, borderRadius: 3,
            padding: '6px 14px', fontFamily: F.mono, fontSize: 10,
            letterSpacing: '0.06em', color: C.dim, textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!chosen || saving}
          style={{
            background: chosen ? C.gold : C.b2,
            border: `1px solid ${chosen ? C.gold : C.b2}`, borderRadius: 3,
            padding: '6px 14px', fontFamily: F.mono, fontSize: 10,
            letterSpacing: '0.06em',
            color: chosen ? '#1a1a1a' : C.dim,
            textTransform: 'uppercase',
            cursor: !chosen || saving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
          }}
        >
          {saving ? 'Saving…' : 'Apply'}
        </button>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────
function ClaimRow({ label, text, accent }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: 10, alignItems: 'baseline' }}>
      <span style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.14em',
        color: accent || C.dim, textTransform: 'uppercase', fontWeight: 600,
      }}>
        {label}
      </span>
      <span style={{
        fontSize: 12, color: C.text, lineHeight: 1.5, fontFamily: F.sans,
        whiteSpace: 'pre-wrap',
      }}>
        {text}
      </span>
    </div>
  );
}

function FieldHeading({ text }) {
  return (
    <div style={{
      fontFamily: F.mono, fontSize: 8, letterSpacing: '0.14em',
      color: C.dim, textTransform: 'uppercase', marginBottom: 4,
    }}>
      {text}
    </div>
  );
}

// One row in the linked-predictions list under a hypothesis card.
// Shows status, claim (truncated), resolution date, and confidence.
function LinkedPredictionRow({ prediction }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const resDate = new Date(prediction.resolution_date);
  const isDue = prediction.status === 'pending' && resDate <= today;
  const displayStatus = isDue ? 'due' : prediction.status;

  const PRED_STATUS_COLOR = {
    pending: C.gold, due: C.amber,
    'resolved-true': C.sage, 'resolved-false': C.terra, inconclusive: C.dim,
  };
  const PRED_STATUS_LABEL = {
    pending: 'PENDING', due: 'DUE',
    'resolved-true': 'CONFIRMED', 'resolved-false': 'REFUTED', inconclusive: 'INCONCLUSIVE',
  };
  const sc = PRED_STATUS_COLOR[displayStatus] || C.dim;
  const sl = PRED_STATUS_LABEL[displayStatus] || displayStatus.toUpperCase();

  return (
    <div style={{
      padding: '6px 8px',
      background: 'rgba(255,255,255,.02)',
      border: `1px solid ${C.b1}`, borderRadius: 3,
      display: 'grid', gridTemplateColumns: '90px 1fr auto', gap: 8,
      alignItems: 'baseline',
    }}>
      <span style={{
        padding: '1px 6px', borderRadius: 2,
        border: `1px solid ${sc}50`,
        background: `${sc}15`, color: sc,
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.06em',
        fontWeight: 600, textTransform: 'uppercase', textAlign: 'center',
      }}>
        {sl}
      </span>
      <span style={{
        fontSize: 11, color: C.mid, lineHeight: 1.4, fontFamily: F.sans,
        overflow: 'hidden', textOverflow: 'ellipsis',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
      }}>
        {prediction.claim}
      </span>
      <span style={{
        fontFamily: F.mono, fontSize: 9, color: C.dim,
        letterSpacing: '0.04em', whiteSpace: 'nowrap',
      }}>
        {formatDate(prediction.resolution_date)}
      </span>
    </div>
  );
}

function FieldBody({ text }) {
  return (
    <div style={{
      fontSize: 11, color: C.mid, lineHeight: 1.5, fontFamily: F.sans,
      whiteSpace: 'pre-wrap',
    }}>
      {text}
    </div>
  );
}

function formatDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return String(d); }
}
